import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Bubble, Button, EmptyTip, Justify, Modal, SelectMultiple, SelectMultipleProps } from 'tea-component';
import { connect } from 'umi';
import {
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
} from '../../connect/multiple';
import {
  IDispatchToProps,
  IMultipleCommonType,
  TOptionsItem,
} from '../../types';
import './index.scss';
import useIntls from '@opm/hooks/useIntls';
import { NO_DATE } from '@/pages/opinion/constants';
import { MultipleSelectButton, renderOptionItem } from './MultipleSelectButton';
import { SpecialSymbol } from '@op/utils/text';
import BubbleCommon, { TipsMapType } from '@op/components/BubbleCommon';

export interface IMultipleSelectType
  extends IDispatchToProps,
  Omit<SelectMultipleProps, 'allOption' | 'options'> {
  options: TOptionsItem[];
  itemRender?: (item: TOptionsItem, isEn: boolean) => string | JSX.Element;
  allOption?: TOptionsItem | false;
  title: string;
  titleCn: string;
  filterClassName?: string;
  maxShow?: number;
  optionsItemRender?: (item: TOptionsItem, isEn: boolean) => string | JSX.Element;
  hideComma?: boolean;
  showItemsThreshold?: number;
  dealSearchText?: (item: TOptionsItem) => string;
  /** 是否在列表顶部显示 tips 区域，默认 false */
  showTips?: boolean;
  /** tips 左侧文案-英文（不使用 title 时可自定义） */
  tipsLeftText?: string;
  /** tips 左侧文案-中文 */
  tipsLeftTextCn?: string;
  /** Clear All 按钮文案-英文，默认 'Clear All' */
  clearAllText?: string;
  /** Clear All 按钮文案-中文，默认 '清除全部' */
  clearAllTextCn?: string;
  /** Select All 按钮文案-英文，默认 'Select All' */
  selectAllText?: string;
  /** Select All 按钮文案-中文，默认 '全选' */
  selectAllTextCn?: string;
  /** 是否显示 Clear All 按钮，默认 showTips 为 true 时默认 true */
  hasClearAll?: boolean;
  /** 是否显示 Select All 切换，默认 showTips 为 true 时默认 true */
  hasSelectAll?: boolean;
  /** 最多可选中的个数，默认不限制 */
  maxSelectCount?: number;
  /** 达到最大选中数时的提示文案-英文，未配置则不展示 */
  maxSelectTooltip?: string;
  /** 达到最大选中数时的提示文案-中文 */
  maxSelectTooltipCn?: string;
  /** 超过最大选中数时全选的提示类型：bubble 气泡提示 | modal 弹窗，默认 bubble */
  maxSelectTipsType?: 'bubble' | 'modal';  // 现在只使用了modal模式，气泡模式可以随意更改
  /** 超过最大选中数时全选的提示文案（TipsMapType 格式，支持中英文） */
  maxSelectTips?: TipsMapType;
  /** modal 弹窗标题-英文，默认 'Notice' */
  maxSelectModalCaption?: string;
  /** modal 弹窗标题-中文，默认 '提示' */
  maxSelectModalCaptionCn?: string;
  /** 透传 Dropdown overlayClassName */
  overlayClassName?: string;
}

function defaultDealSearchText(item: TOptionsItem) {
  return `${item?.text?.toLowerCase()}${SpecialSymbol.LEFT}${item?.textCn?.toLowerCase()}`;
}

/** 判断选项是否匹配搜索关键词（tea-component filter / filteredCount / filteredOptions 共用） */
function matchFilter(inputValue: string, option: any) {
  if (!inputValue.trim()) return true;
  return option.searchText?.includes(inputValue.toLowerCase());
}

export function MultipleSelect({
  options,
  itemRender,
  allOption = { text: 'All', textCn: '全部', value: 'all' },
  change,
  value,
  title,
  titleCn,
  filterClassName,
  maxShow,
  dealSearchText = defaultDealSearchText,
  optionsItemRender,
  hideComma,
  showItemsThreshold,
  showTips = false,
  tipsLeftText: tipsLeftTextProp,
  tipsLeftTextCn: tipsLeftTextCnProp,
  clearAllText: clearAllTextProp = 'Clear All',
  clearAllTextCn: clearAllTextCnProp = '清除全部',
  selectAllText: selectAllTextProp = 'Select All',
  selectAllTextCn: selectAllTextCnProp = '全选',
  hasClearAll: hasClearAllProp,
  hasSelectAll: hasSelectAllProp,
  maxSelectCount,
  maxSelectTooltip: maxSelectTooltipProp,
  maxSelectTooltipCn: maxSelectTooltipCnProp,
  maxSelectTipsType = 'bubble',
  maxSelectTips,
  maxSelectModalCaption: maxSelectModalCaptionProp = 'Notice',
  maxSelectModalCaptionCn: maxSelectModalCaptionCnProp = '提示',
  overlayClassName,
  ...props
}: IMultipleSelectType & IMultipleCommonType) {
  const { intl, isEn } = useIntls();

  // Track search input to detect no-result state
  const [searchText, setSearchText] = useState('');

  // Whether user is actively searching
  const isSearching = searchText.trim().length > 0;

  // Control max select tip visibility
  const [showMaxSelectBubble, setShowMaxSelectBubble] = useState(false);
  const [showMaxSelectModal, setShowMaxSelectModal] = useState(false);
  const bubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup bubble timer on unmount
  useEffect(() => {
    return () => {
      if (bubbleTimerRef.current) {
        clearTimeout(bubbleTimerRef.current);
      }
    };
  }, []);

  const allOptionMemo = useMemo(() => {
    if (!allOption) return undefined;
    return {
      text: isEn ? allOption.text : allOption.textCn,
      value: allOption.value,
    };
  }, [intl]);

  // Determine if Clear All / Select All should be shown
  const hasClearAll = hasClearAllProp ?? showTips;
  const hasSelectAll = hasSelectAllProp ?? showTips;
  // Only show Select All toggle when both Clear All and Select All are enabled
  const showSelectAllToggle = hasClearAll && hasSelectAll;
  // Max select tooltip text (supports i18n)
  const maxSelectTooltip = useMemo(() => {
    if (maxSelectTooltipProp || maxSelectTooltipCnProp) {
      return isEn
        ? (maxSelectTooltipProp ?? maxSelectTooltipCnProp)
        : (maxSelectTooltipCnProp ?? maxSelectTooltipProp);
    }
    return undefined;
  }, [isEn, maxSelectTooltipProp, maxSelectTooltipCnProp]);


  const isAllSelected = useMemo(() => {
    if (options.length === 0) return false;
    // 有最大选中数限制时：选中数量 >= min(n, 选项总数) 即视为"已全选"
    if (maxSelectCount !== undefined) {
      return value.length >= Math.min(maxSelectCount, options.length);
    }
    return value.length === options.length;
  }, [value, options, maxSelectCount]);

  // Light path: no max selection limit — no value dependency, skips recomputation on value change
  const renderOptionsLight = useMemo(() => {
    return options.map((item) => ({
      ...item,
      searchText: dealSearchText(item),
      text: renderOptionItem(item, isEn, optionsItemRender || itemRender),
    }));
  }, [options, isEn]);

  // Full path: with max selection limit — compute disabled state and tooltip per item
  const renderOptionsFull = useMemo(() => {
    if (maxSelectCount === undefined) return undefined;

    const isMaxReached = value.length >= maxSelectCount!;
    const hasTooltip = !!maxSelectTooltip;

    return options.map((item) => {
      const isSelected = value.includes(item.value);
      const isDisabled = isMaxReached && !isSelected;

      let displayText = renderOptionItem(item, isEn, optionsItemRender || itemRender);

      if (isDisabled && hasTooltip) {
        displayText = (
          <Bubble placement="top" content={maxSelectTooltip}>
            <span className="dbom-option-disabled">{displayText}</span>
          </Bubble>
        );
      }

      return {
        ...item,
        disabled: isDisabled,
        searchText: dealSearchText(item),
        text: displayText,
      };
    });
  }, [options, value, maxSelectCount, maxSelectTooltip, isEn]);

  // Choose the appropriate option list based on whether max limit is configured
  const renderOptions = useMemo(() => {
    return (maxSelectCount === undefined ? renderOptionsLight : renderOptionsFull) ?? [];
  }, [renderOptionsLight, renderOptionsFull]);

  // Options matching current search text (consistent with tea-component's filter prop)
  const filteredOptions = useMemo(() => {
    if (!isSearching) return renderOptions;
    return renderOptions.filter((opt: any) => matchFilter(searchText, opt));
  }, [searchText, renderOptions, isSearching]);
  const filteredCount = filteredOptions.length;

  // Handle Clear All / Select All click
  const handleToggleAll = useCallback(() => {
    if (isSearching) {
      if (isAllSelected) {
        // 清除：始终清空所有已选项
        change([]);
      } else {
        // 搜索 + 全选：搜索结果与当前已选取并集，受 maxSelectCount 限制
        const targetValues = filteredOptions.map((item) => item.value);
        const merged = [...new Set([...value, ...targetValues])];
        if (maxSelectCount !== undefined && merged.length > maxSelectCount) {
          change(merged.slice(0, maxSelectCount));
          if (maxSelectTips) {
            if (maxSelectTipsType === 'modal') {
              setShowMaxSelectModal(true);
            } else {
              setShowMaxSelectBubble(true);
              if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
              bubbleTimerRef.current = setTimeout(() => setShowMaxSelectBubble(false), 3000);
            }
          }
        } else {
          change(merged);
        }
      }
      return;
    }

    if (isAllSelected) {
      change([]);
      return;
    }

    // If maxSelectCount is set, only select up to the limit
    if (maxSelectCount !== undefined && options.length > maxSelectCount) {
      change(options.slice(0, maxSelectCount).map((item) => item.value));

      // Show tip when max limit is reached via Select All
      if (maxSelectTips) {
        if (maxSelectTipsType === 'modal') {
          setShowMaxSelectModal(true);
        } else {
          // bubble: show and auto-hide after 3 seconds
          setShowMaxSelectBubble(true);
          if (bubbleTimerRef.current) {
            clearTimeout(bubbleTimerRef.current);
          }
          bubbleTimerRef.current = setTimeout(() => {
            setShowMaxSelectBubble(false);
          }, 3000);
        }
      }
    } else if (maxSelectCount !== undefined) {
      change(options.slice(0, maxSelectCount).map((item) => item.value));
    } else {
      change(options.map((item) => item.value));
    }
  }, [isSearching, filteredOptions, isAllSelected, value, options, maxSelectCount, maxSelectTips, maxSelectTipsType, change]);

  // Tips left text: use custom text (supports i18n) or fall back to title
  const tipsLeftText = useMemo(() => {
    if (tipsLeftTextProp || tipsLeftTextCnProp) {
      return isEn
        ? (tipsLeftTextProp ?? tipsLeftTextCnProp)
        : (tipsLeftTextCnProp ?? tipsLeftTextProp);
    }
    return isEn ? title : titleCn;
  }, [isEn, tipsLeftTextProp, tipsLeftTextCnProp, title, titleCn]);


  // Resolve i18n text for buttons
  const { clearAllText, selectAllText } = useMemo(() => ({
    clearAllText: isEn ? clearAllTextProp : clearAllTextCnProp,
    selectAllText: isEn ? selectAllTextProp : selectAllTextCnProp,
  }), [isEn, clearAllTextProp, clearAllTextCnProp, selectAllTextProp, selectAllTextCnProp]);

  // Build the right side buttons for tips
  const renderTipsRight = useCallback(() => {
    // Wrap button in Bubble tip when maxSelectTips bubble type is needed
    const wrapWithBubble = (btn: JSX.Element) => {
      if (maxSelectTips && maxSelectTipsType === 'bubble') {
        return (
          <BubbleCommon
            tipsMap={maxSelectTips}
            visible={showMaxSelectBubble}
            placement="top"
            showIcon={false}
          >
            {btn}
          </BubbleCommon>
        );
      }
      return btn;
    };

    if (showSelectAllToggle) {
      return wrapWithBubble(
        <Button type="link" onClick={handleToggleAll}>
          {isAllSelected ? clearAllText : selectAllText}
        </Button>,
      );
    }
    if (hasClearAll) {
      const handleClearAll = () => {
        change([]);
      };
      return (
        <Button type="link" onClick={handleClearAll}>
          {clearAllText}
        </Button>
      );
    }
    if (hasSelectAll) {
      const handleSelectAll = () => {
        if (isSearching) {
          const targetValues = filteredOptions.map((item) => item.value);
          const merged = [...new Set([...value, ...targetValues])];
          if (maxSelectCount !== undefined && merged.length > maxSelectCount) {
            change(merged.slice(0, maxSelectCount));
            if (maxSelectTips) {
              if (maxSelectTipsType === 'modal') {
                setShowMaxSelectModal(true);
              } else {
                setShowMaxSelectBubble(true);
                if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
                bubbleTimerRef.current = setTimeout(() => setShowMaxSelectBubble(false), 3000);
              }
            }
          } else {
            change(merged);
          }
          return;
        }
        if (maxSelectCount !== undefined && options.length > maxSelectCount) {
          change(options.slice(0, maxSelectCount).map((item) => item.value));
          // Show tip
          if (maxSelectTips) {
            if (maxSelectTipsType === 'modal') {
              setShowMaxSelectModal(true);
            } else {
              setShowMaxSelectBubble(true);
              if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
              bubbleTimerRef.current = setTimeout(() => setShowMaxSelectBubble(false), 3000);
            }
          }
        } else if (maxSelectCount !== undefined) {
          change(options.slice(0, maxSelectCount).map((item) => item.value));
        } else {
          change(options.map((item) => item.value));
        }
      };
      return wrapWithBubble(
        <Button type="link" onClick={handleSelectAll}>
          {selectAllText}
        </Button>,
      );
    }
    return null;
  }, [
    showSelectAllToggle,
    hasClearAll,
    hasSelectAll,
    isAllSelected,
    clearAllText,
    selectAllText,
    handleToggleAll,
    options,
    maxSelectCount,
    maxSelectTips,
    maxSelectTipsType,
    showMaxSelectBubble,
    change,
    isSearching,
    filteredOptions,
    value,
  ]);

  // Build the tips content
  const tipsContent = useMemo(() => {
    // When no options or search yields no results, show EmptyTip with NO_DATE.TITLE
    if (filteredCount === 0) {
      return <EmptyTip style={{ height: 70, lineHeight: "50px", textAlign: "center" }} emptyText={intl(NO_DATE.TITLE)} />;
    }
    if (!showTips) return undefined;

    return (
      <Justify
        className='dbom-tips-content'
        left={<span className='dbom-tips-left'>{tipsLeftText}</span>}
        right={
          <span className="dbom-tips-right">
            {renderTipsRight()}
          </span>
        }
      />
    );
  }, [showTips, filteredCount, tipsLeftText, renderTipsRight, intl]);

  // Handle search input change
  const handleSearch = useCallback((keyword?: string) => {
    setSearchText(keyword ?? '');
  }, []);

  // Handle onChange
  const handleChange = useCallback((newValue: string[]) => {
    change(newValue);
  }, [change]);

  // Resolve maxSelectTips content for modal display
  const maxSelectTipsContent = useMemo(() => {
    if (!maxSelectTips) return null;
    const { sort, space, ...languageMap } = maxSelectTips;
    const langKey = isEn ? 'en' : 'zh';
    return languageMap[langKey] ?? null;
  }, [maxSelectTips, isEn]);

  return (
    <>
      {/* Modal for max select tips */}
      {maxSelectTips && maxSelectTipsType === 'modal' && (
        <Modal
          visible={showMaxSelectModal}
          onClose={() => setShowMaxSelectModal(false)}
          caption={isEn ? maxSelectModalCaptionProp : maxSelectModalCaptionCnProp}
          disableCloseIcon={false}
        >
          <Modal.Body>{maxSelectTipsContent}</Modal.Body>
          <Modal.Footer><Button type="primary" onClick={() => setShowMaxSelectModal(false)}>{intl("Confirm")}</Button></Modal.Footer>
        </Modal>
      )}

      <div className={`dbom-multiple-select-wrapper ${filterClassName}`}>
        <SelectMultiple
          matchButtonWidth
          appearance="button"
          overlayClassName={`dbom-multiple-select-overlay ${overlayClassName}`}
          options={renderOptions}
          staging={false}
          allOption={allOptionMemo}
          filter={matchFilter}
          tips={tipsContent}
          button={<MultipleSelectButton
            value={value}
            options={options}
            title={title}
            titleCn={titleCn}
            itemRender={itemRender}
            maxShow={maxShow}
            hideComma={hideComma}
            showItemsThreshold={showItemsThreshold}
          />}
          value={value}
          onChange={handleChange}
          searchPlaceholder={intl("Search")}
          onSearchValueChange={handleSearch}
          {...props}
        />
      </div>
    </>
  );
}

export default connect(
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
)(MultipleSelect);
